import { useEffect, useRef, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { useRegisterModal } from '@/context/ModalContext'
import { toast } from 'sonner'
import { Loader2 } from 'lucide-react'

interface CreateKnowledgeCollectionDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  workspaceId: string
  onCreated?: (sourceSlug: string) => void
}

export function CreateKnowledgeCollectionDialog({
  open,
  onOpenChange,
  workspaceId,
  onCreated,
}: CreateKnowledgeCollectionDialogProps) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [about, setAbout] = useState('')
  const [icon, setIcon] = useState('📘')
  const [creating, setCreating] = useState(false)
  const nameInputRef = useRef<HTMLInputElement>(null)

  useRegisterModal(open, () => onOpenChange(false))

  useEffect(() => {
    if (open) {
      setName('')
      setDescription('')
      setAbout('')
      setIcon('📘')
      const timer = setTimeout(() => nameInputRef.current?.focus(), 0)
      return () => clearTimeout(timer)
    }
  }, [open])

  const handleSubmit = async () => {
    const trimmedName = name.trim()
    if (!trimmedName || creating) return

    setCreating(true)
    try {
      const result = await window.electronAPI.knowledgeCreateCollection(workspaceId, {
        name: trimmedName,
        description: description.trim() || undefined,
        about: about.trim() || undefined,
        icon: icon.trim() || undefined,
      })

      if (result.success && result.sourceSlug) {
        toast.success('Collection created', { description: trimmedName })
        onOpenChange(false)
        onCreated?.(result.sourceSlug)
      } else {
        toast.error('Failed to create collection', {
          description: result.error || 'Unknown error',
        })
      }
    } catch (err) {
      toast.error('Failed to create collection', {
        description: err instanceof Error ? err.message : 'Unknown error',
      })
    } finally {
      setCreating(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[440px]" onOpenAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>New Knowledge Collection</DialogTitle>
          <DialogDescription>
            Create a new collection and connect it to this workspace.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4 py-2">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="collection-name">Title</Label>
            <Input
              id="collection-name"
              ref={nameInputRef}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Engineering Guidelines"
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSubmit()
              }}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="collection-icon">Icon</Label>
            <div className="flex items-center gap-2">
              <Input
                id="collection-icon"
                value={icon}
                onChange={(e) => setIcon(e.target.value)}
                placeholder="📘"
                className="w-16 text-center text-lg"
              />
              <span className="text-xs text-muted-foreground">Emoji</span>
            </div>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="collection-description">Description <span className="text-muted-foreground font-normal">(optional)</span></Label>
            <Input
              id="collection-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Short tagline for this collection"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="collection-about">About <span className="text-muted-foreground font-normal">(optional)</span></Label>
            <Textarea
              id="collection-about"
              value={about}
              onChange={(e) => setAbout(e.target.value)}
              placeholder="Longer description of what this collection contains…"
              rows={3}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={creating}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!name.trim() || creating}>
            {creating && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            Create Collection
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
